import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { fetchMasterData } from "../api/masterData";
import {
  buildAllowedFilterIds,
  buildFilterFields,
} from "../data/quick-sheets/filters";
import {
  clearFilterRegistry,
  setFilterRegistry,
} from "../data/quick-sheets/filterRegistry";

const MasterDataContext = createContext(null);

function mapCurriculumOptions(standards = []) {
  return standards.map((item) => ({
    id: item.id,
    label: item.name,
    subtitle: item.description ?? null,
  }));
}

function mapSubjectOptions(subjects = []) {
  return subjects.map((item) => ({
    id: item.id,
    label: item.name,
  }));
}

export function MasterDataProvider({ children }) {
  const [standards, setStandards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadMasterData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = await fetchMasterData();
      setStandards(payload.standards ?? []);
      setSubjects(payload.subjects ?? []);
    } catch (loadError) {
      setStandards([]);
      setSubjects([]);
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Failed to load curriculum and subject options.",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMasterData();
    return () => {
      clearFilterRegistry();
    };
  }, [loadMasterData]);

  const filterFields = useMemo(
    () => buildFilterFields({ standards, subjects }),
    [standards, subjects],
  );

  const allowedFilterIds = useMemo(
    () => buildAllowedFilterIds(filterFields),
    [filterFields],
  );

  useEffect(() => {
    setFilterRegistry({
      fields: filterFields,
      allowedIds: allowedFilterIds,
    });
  }, [filterFields, allowedFilterIds]);

  const resolveFilterLabel = useCallback(
    (fieldId, optionId) => {
      const field = filterFields.find((item) => item.id === fieldId);
      const option = field?.options.find((item) => item.id === optionId);
      return option?.label ?? optionId;
    },
    [filterFields],
  );

  const areFiltersComplete = useCallback(
    (filters = {}) => filterFields.every((field) => Boolean(filters[field.id])),
    [filterFields],
  );

  const buildFilterLabels = useCallback(
    (filters = {}) => ({
      curriculum: resolveFilterLabel("curriculum", filters.curriculum ?? ""),
      grade: resolveFilterLabel("grade", filters.grade ?? ""),
      subject: resolveFilterLabel("subject", filters.subject ?? ""),
    }),
    [resolveFilterLabel],
  );

  const value = useMemo(
    () => ({
      standards,
      subjects,
      curriculumOptions: mapCurriculumOptions(standards),
      subjectOptions: mapSubjectOptions(subjects),
      filterFields,
      allowedFilterIds,
      isLoading,
      error,
      reload: loadMasterData,
      resolveFilterLabel,
      areFiltersComplete,
      buildFilterLabels,
      isReady: !isLoading && !error && standards.length > 0 && subjects.length > 0,
    }),
    [
      standards,
      subjects,
      filterFields,
      allowedFilterIds,
      isLoading,
      error,
      loadMasterData,
      resolveFilterLabel,
      areFiltersComplete,
      buildFilterLabels,
    ],
  );

  return (
    <MasterDataContext.Provider value={value}>{children}</MasterDataContext.Provider>
  );
}

export function useMasterData() {
  const context = useContext(MasterDataContext);
  if (!context) {
    throw new Error("useMasterData must be used within MasterDataProvider");
  }
  return context;
}
